import React, { useEffect, useState } from 'react';
import {
  MdModeEditOutline,
  MdSchool,
  MdWork,
  MdBook,
  MdPayment,
  MdLocationOn,
  MdInfo,
} from 'react-icons/md';
import Card from 'components/card';
import userService from 'ApiService/Student/userService';
import ProfilePic from '../ProfilePic/ProfilePic';
import PasswordChangeModal from 'pages/Model/PasswordChangeModal';

const DEFAULT_PROFILE_PIC = '';

const StudentProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await userService.ProfileDetails();
        if (response.data.success) {
          setProfile(response.data.student);
        } else {
          throw new Error(response.data.message || 'Failed to fetch profile');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch profile');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-t-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card extra="w-full p-6 h-full">
        <div className="text-center text-red-500">{error}</div>
      </Card>
    );
  }

  if (!profile) {
    return (
      <Card extra="w-full p-6 h-full">
        <div className="text-center text-gray-500">
          No profile information available
        </div>
      </Card>
    );
  }

  const formatDate = (dateString) => {
    return dateString
      ? new Date(dateString).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : 'N/A';
  };

  return (
    <div className="space-y-6">
      {/* Basic Profile Section */}
      <Card extra="w-full p-6">
        <div className="mb-6 flex items-center space-x-4">
          <ProfilePic />
          <div className="flex-grow">
            <p className="text-xl font-bold text-navy-700 dark:text-white">
              {profile.name}
            </p>
            <p className="text-gray-600 dark:text-white">{profile.email}</p>
            <p className="text-gray-600 dark:text-white">
              {profile.contactNumber}
            </p>
          </div>
        </div>
      </Card>

      <Card extra="w-full p-6">
        <div className="mb-4 flex items-center">
          <MdLocationOn className="mr-2 text-2xl text-teal-600" />
          <h2 className="text-xl font-semibold text-navy-700 dark:text-white">
            Address Details
          </h2>
        </div>
        {profile.address ? (
          <div className="rounded-xl bg-gray-100 p-4 dark:bg-navy-700">
            <div className="space-y-2">
              <div className="flex justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Street
                </p>
                <p className="font-medium text-navy-700 dark:text-white">
                  {profile.address.street || 'N/A'}
                </p>
              </div>
              <div className="flex justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-300">City</p>
                <p className="font-medium text-navy-700 dark:text-white">
                  {profile.address.city || 'N/A'}
                </p>
              </div>
              <div className="flex justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  State
                </p>
                <p className="font-medium text-navy-700 dark:text-white">
                  {profile.address.state || 'N/A'}
                </p>
              </div>
              <div className="flex justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Postal Code
                </p>
                <p className="font-medium text-navy-700 dark:text-white">
                  {profile.address.postalCode || 'N/A'}
                </p>
              </div>
              <div className="flex justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Country
                </p>
                <p className="font-medium text-navy-700 dark:text-white">
                  {profile.address.country || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-500">
            No address details available
          </p>
        )}
      </Card>

      {/* Additional Personal Details Section */}
      <Card extra="w-full p-6">
        <div className="mb-4 flex items-center">
          <MdModeEditOutline className="mr-2 text-2xl text-indigo-600" />
          <h2 className="text-xl font-semibold text-navy-700 dark:text-white">
            Personal Information
          </h2>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Date of Birth
            </p>
            <p className="font-medium text-navy-700 dark:text-white">
              {formatDate(profile.dateOfBirth)}
            </p>
          </div>
          <div className="flex justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">Gender</p>
            <p className="font-medium text-navy-700 dark:text-white">
              {profile.gender || 'N/A'}
            </p>
          </div>
          <div className="flex justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Parent Name
            </p>
            <p className="font-medium text-navy-700 dark:text-white">
              {profile.parentName || 'N/A'}
            </p>
          </div>
          <div className="flex justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Parent Contact Number
            </p>
            <p className="font-medium text-navy-700 dark:text-white">
              {profile.ParentContactNo || 'N/A'}
            </p>
          </div>
        </div>
      </Card>

      {/* Placement and Branch Details Section */}
      <Card extra="w-full p-6">
        <div className="mb-4 flex items-center">
          <MdWork className="mr-2 text-2xl text-orange-600" />
          <h2 className="text-xl font-semibold text-navy-700 dark:text-white">
            Placement & Branch Details
          </h2>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">Branch</p>
            <p className="font-medium text-navy-700 dark:text-white">
              {profile.branch?.name || 'N/A'}
            </p>
          </div>
          <div className="flex justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Branch Location
            </p>
            <p className="font-medium text-navy-700 dark:text-white">
              {profile.branch?.location || 'N/A'}
            </p>
          </div>
          <div className="flex justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Job Location Preference
            </p>
            <p className="font-medium text-navy-700 dark:text-white">
              {profile.jobLocationPreference?.join(', ') || 'N/A'}
            </p>
          </div>
          <div className="flex justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Placement Priority
            </p>
            <p className="font-medium text-navy-700 dark:text-white">
              {profile.placementPriority || 'N/A'}
            </p>
          </div>
          <div className="flex justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Ready to Place
            </p>
            <p
              className={`font-medium ${
                profile.readyToPlace ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {profile.readyToPlace ? 'Yes' : 'No'}
            </p>
          </div>
        </div>
      </Card>

      {/* Academic Records Section */}

      <Card extra="w-full p-6">
        <div className="mb-4 flex items-center">
          <MdSchool className="mr-2 text-2xl text-blue-600" />
          <h2 className="text-xl font-semibold text-navy-700 dark:text-white">
            Academic Records
          </h2>
        </div>
        {profile.academicRecords && profile.academicRecords.length > 0 ? (
          <div className="space-y-4">
            {profile.academicRecords.map((record, index) => (
              <div
                key={index}
                className="rounded-xl bg-gray-100 p-4 dark:bg-navy-700"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-navy-700 dark:text-white">
                      {record.level} - {record.institutionName}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Board: {record.board} | Year: {record.yearOfCompletion}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-navy-700 dark:text-white">
                      {record.percentage ? `${record.percentage}%` : 'N/A'}
                    </p>
                    {record.gpa && (
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        GPA: {record.gpa}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500">No academic records found</p>
        )}
      </Card>

      {/* Work Experience Section */}
      <Card extra="w-full p-6">
        <div className="mb-4 flex items-center">
          <MdWork className="mr-2 text-2xl text-green-600" />
          <h2 className="text-xl font-semibold text-navy-700 dark:text-white">
            Work Experience
          </h2>
        </div>
        {profile.workExperience && profile.workExperience.length > 0 ? (
          <div className="space-y-4">
            {profile.workExperience.map((exp, index) => (
              <div
                key={index}
                className="rounded-xl bg-gray-100 p-4 dark:bg-navy-700"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-navy-700 dark:text-white">
                      {exp.position} at {exp.companyName}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {formatDate(exp.startDate)} - {formatDate(exp.endDate)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {exp.skills}
                    </p>
                  </div>
                </div>
                {exp.responsibilities && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    {exp.responsibilities}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500">No work experience found</p>
        )}
      </Card>

      {/* Enrollments Section */}
      <Card extra="w-full p-6">
        <div className="mb-4 flex items-center">
          <MdBook className="mr-2 text-2xl text-purple-600" />
          <h2 className="text-xl font-semibold text-navy-700 dark:text-white">
            Course Enrollments
          </h2>
        </div>
        {profile.enrollments && profile.enrollments.length > 0 ? (
          <div className="space-y-4">
            {profile.enrollments.map((enrollment, index) => (
              <div
                key={index}
                className="rounded-xl bg-gray-100 p-4 dark:bg-navy-700"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-navy-700 dark:text-white">
                      {enrollment.course?.courseName}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Batch: {enrollment.batch?.batchNumber}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`font-semibold ${
                        enrollment.status === 'Active'
                          ? 'text-green-600'
                          : 'text-gray-600'
                      }`}
                    >
                      {enrollment.status}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Enrolled: {formatDate(enrollment.enrollmentDate)}
                    </p>
                  </div>
                </div>
                {enrollment.notes && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    {enrollment.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500">No enrollments found</p>
        )}
      </Card>

      {/* Fee Details Section */}
      <Card extra="w-full p-6">
        <div className="mb-4 flex items-center">
          <MdPayment className="mr-2 text-2xl text-red-600" />
          <h2 className="text-xl font-semibold text-navy-700 dark:text-white">
            Fee Details
          </h2>
        </div>
        {profile.fees ? (
          <div className="space-y-4">
            <div className="rounded-xl bg-gray-100 p-4 dark:bg-navy-700">
              <div className="mb-2 flex items-center justify-between">
                <p className="font-medium text-navy-700 dark:text-white">
                  Total Course Fee
                </p>
                <p className="font-semibold text-navy-700 dark:text-white">
                  ₹{profile.fees.totalAmount}
                </p>
              </div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Total Paid
                </p>
                <p className="text-sm text-green-600">
                  ₹{profile.fees.totalPaid}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Remaining Amount
                </p>
                <p className="text-sm text-red-600">
                  ₹{profile.fees.remainingAmount}
                </p>
              </div>
            </div>

            {/* Installments */}
            <div>
              <h3 className="mb-2 text-lg font-semibold text-navy-700 dark:text-white">
                Installment Details
              </h3>
              {profile.fees.installments &&
              profile.fees.installments.length > 0 ? (
                <div className="space-y-2">
                  {profile.fees.installments.map((installment, index) => (
                    <div
                      key={index}
                      className="rounded-lg border bg-white p-3 dark:bg-navy-800"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Installment {index + 1}
                        </p>
                        <div>
                          <p
                            className={`text-sm font-semibold ${
                              installment.isPaid
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            {installment.isPaid ? 'Paid' : 'Pending'}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            Amount: ₹{installment.amount}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            Due: {formatDate(installment.dueDate)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500">
                  No installment details found
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-500">No fee details available</p>
        )}
      </Card>
      <div className="mt-2 flex w-full justify-end">
        <PasswordChangeModal />
      </div>
      <div className="mt-4 flex items-center  rounded-r-lg  p-3">
        <MdInfo className="mr-3 text-2xl text-yellow-600" />
        <p className="text-sm text-yellow-700">
          Need to update any details? Please contact your branch admin.
        </p>
      </div>
    </div>
  );
};

export default StudentProfile;
